/**
 * @fileoverview The Active Clients grid's footer and pager.
 * @description "Showing 1-25 of 61 clients", with Previous and Next and "Page X of Y" from the
 * backend's paging meta. The counts are the backend's, scoped to the same filters as the rows.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import type { ClientListMeta } from '@/features/sales/types/sales-client.types'

interface ClientsPagerProps {
  meta: ClientListMeta
  /** How many rows this page shows. */
  shown: number
  onPage: (page: number) => void
}

const BUTTON_CLASS =
  'cursor-pointer rounded-lg border border-input-border bg-white px-3 py-1.5 text-xs font-semibold text-slate hover:border-border-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-50'

/**
 * @component
 * @param {ClientsPagerProps} props - The paging meta and the handler.
 * @param {ClientListMeta} props.meta - The page, page size, total and page count.
 * @param {number} props.shown - How many rows the page shows.
 * @param {(page: number) => void} props.onPage - Called with the page to go to.
 */
function ClientsPager({ meta, shown, onPage }: ClientsPagerProps) {
  const { t } = useTranslation()
  const first = shown === 0 ? 0 : (meta.page - 1) * meta.limit + 1
  const totalPages = Math.max(meta.totalPages, 1)

  /**
   * @description Goes to the previous page.
   */
  const previous = () => onPage(meta.page - 1)

  /**
   * @description Goes to the next page.
   */
  const next = () => onPage(meta.page + 1)

  return (
    <div
      className="flex flex-wrap items-center gap-3 border-t border-divider bg-surface-hover px-4 py-2.5 sm:px-5"
      data-testid="active-clients-footer"
    >
      <span className="flex-1 text-xs text-slate" data-testid="active-clients-footer-line">
        {t('sales.activeClients.footerLine', {
          from: first,
          to: first === 0 ? 0 : first + shown - 1,
          total: meta.total,
        })}
      </span>
      <button
        type="button"
        className={BUTTON_CLASS}
        disabled={meta.page <= 1}
        onClick={previous}
        data-testid="active-clients-previous-button"
      >
        {t('sales.activeClients.pager.previous')}
      </button>
      <span className="text-xs font-medium text-placeholder" data-testid="active-clients-page">
        {t('sales.activeClients.pager.pageOfPages', { page: meta.page, total: totalPages })}
      </span>
      <button
        type="button"
        className={BUTTON_CLASS}
        disabled={meta.page >= totalPages}
        onClick={next}
        data-testid="active-clients-next-button"
      >
        {t('sales.activeClients.pager.next')}
      </button>
    </div>
  )
}

export default ClientsPager
